/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ActivityDay,
  ANTHROPIC_TZ,
  parseActivityResponse,
} from './claudeMetricsView';

/**
 * Reads the last-30-days ingest activity for the trust chart beside the
 * Reporting status table. Days are the viewer's calendar days, same as the
 * day/month views.
 *
 * Degrades rather than fails: the chart is a companion to the table, and
 * losing it must not cost the panel. But the failure is REPORTED (errorApi),
 * not swallowed -- the panel's own principle is that absence is data.
 * `reload` re-runs the read, same contract as useClaudeMetrics.
 */
export const useClaudeActivity = (
  reload = 0,
  tz: string = ANTHROPIC_TZ,
): { days?: ActivityDay[] } => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);
  const [days, setDays] = useState<ActivityDay[]>();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('claude-metrics');
        const res = await fetchApi.fetch(
          `${base}/activity?${new URLSearchParams({ tz })}`,
        );
        // 404 stays SILENT on purpose: a server one deploy behind has no
        // /activity route, and the rollout window must not toast an error at
        // every admin. Anything else is a real failure and gets reported.
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`request failed (${res.status})`);
        const parsed = parseActivityResponse(await res.json());
        if (!parsed) throw new Error('activity payload unreadable');
        if (active) setDays(parsed.days);
      } catch (e) {
        if (active) setDays(undefined);
        errorApi.post(
          new Error(
            `Claude usage: ingest activity unavailable (${
              e instanceof Error ? e.message : e
            })`,
          ),
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi, errorApi, reload, tz]);

  return { days };
};
